import { useState } from "react";
import { useDispatch, useStore } from "react-redux";
import { useLocation } from "react-router";
import useNavigateWEB from "./useNavigateWEB";
import useConfig, { type Config } from "~/shared/api/useConfig";
import dispatchers from "~/shared/dispatchers";
import type { State } from "~/shared/reducers";
import { persistQueryClientRestore } from "@tanstack/react-query-persist-client";
import { useQueryClient } from "@tanstack/react-query";
import { persister } from "../use-query/QueryClientProvider";
import useSoftReRender from "../use-soft-re-render";
import mbc, { type StackType } from "~/shared/functions/mbc";
import { mbcStorage } from "~/shared/mbc/mbcStorage";

type OpenWebPagePromise = Awaited<ReturnType<typeof mbc.open>>;

export function getUrl(link: string): string {
	const origin = window.location.origin;
	const baseUrl = `${origin}/privat/forsikring/pensjon`;
	if (link.startsWith("/")) {
		return `${baseUrl}${link}`;
	}
	if (link.startsWith("../")) {
		const split = link.split("/");
		const newLink = split.filter((s) => s !== "..").join("/");
		const goBackNum = split.filter((s) => s === "..").length;

		const pathSplit = window.location.pathname.split("/");
		const url = pathSplit.slice(0, pathSplit.length - goBackNum).join("/");

		return `${origin}${url}/${newLink}`;
	}
	/*
		I native er det en bug hvor man kan ende opp med to skråstreker etter hverandre i url-en, f.eks
		'privat/forsikring/pensjon/sl/anbefaling//oppsummering' hvor etter å ha endret invisteringsvalg
		navigerer vi tilbake til privat/forsikring/pensjon/sl/anbefaling/ med navigate('..').
		Dette legger til en ekstra "/". Da klarer ikke appen å skjønne navigering til oppsummering.
		.replace regex fjerner dobble skråstreker (bortsett fra http(s)://)
	*/
	const cleanedUrl = `${origin}${window.location.pathname}/${link}`.replace(/([^:]\/)\/+/g, "$1");
	return cleanedUrl;
}

interface MobilBankState {
	config: Config;
	reduxState: State;
	ikkeForsteSide: boolean;
	iExterntLag: boolean;
	storage: object;
	debugLogs: Log[];
	navigasjonState?: object;
}

export function getMobilBankState() {
	return mbc.getInitialState() as MobilBankState;
}

export interface MbcOptions {
	presentationStyle?: StackType;
	authenticationUpgradeRequired?: boolean;
	hideCloseButton?: boolean;
	hideNavigationBar?: boolean;
	navigationBarTitle?: string;
	closeEntireExternalLayer?: boolean;
	onReturn?(data: OpenWebPagePromise): void;
	replace?: boolean;
	state?: object;
}

function useNavigateMBC() {
	const config = useConfig();
	const reduxStore = useStore<State>();
	const queryClient = useQueryClient();
	const softReRender = useSoftReRender();
	const dispatch = useDispatch();
	const mobilState = getMobilBankState();
	const navigateWeb = useNavigateWEB();
	const location = useLocation();

	const navigate = (link: string | number, { navigationBarTitle = "Pensjon", closeEntireExternalLayer, replace, onReturn, state, presentationStyle, ...options }: MbcOptions = {}) => {
		const reduxState = reduxStore.getState();

		const tilOversiktsSiden = () => {
			if (mobilState.ikkeForsteSide) {
				const callback = () => {
					return { reduxState, storage: mbcStorage.storage, debugLogs: debugLogger.logs, toOversikt: true };
				};
				mbc.onClosed(callback);
				mbc.close();
			} else if (location.pathname !== "/") {
				navigateWeb("/");
			}
		};

		if (typeof link === "number") {
			mbc.close();
		} else if (link === "/") {
			tilOversiktsSiden();
		} else if (closeEntireExternalLayer || replace) {
			// burde replace kanskje gjøres via vanlig web navigasjon?
			const callback = () => {
				return { reduxState, storage: mbcStorage.storage, debugLogs: debugLogger.logs, redirect: { link, options: { navigationBarTitle, ...options } } };
			};
			mbc.onClosed(callback);
			if (closeEntireExternalLayer) mbc.close(true);
			else mbc.close();
		} else {
			const initialState: MobilBankState = {
				config,
				reduxState,
				ikkeForsteSide: true,
				iExterntLag: presentationStyle === "EXTERNAL",
				storage: mbcStorage.storage,
				debugLogs: debugLogger.logs,
				navigasjonState: state,
			};
			mbc.open(getUrl(link), navigationBarTitle, presentationStyle, { ...options, initialState }).then(async (data) => {
				const state = data.state as any;

				mbcStorage.storage = state.storage;
				debugLogger.updateLogs(state.debugLogs);
				dispatchers.initStore(state.reduxState)(dispatch);
				await persistQueryClientRestore({ queryClient, persister });
				softReRender();

				if (state.toOversikt) {
					tilOversiktsSiden();
				} else if (state.redirect && getUrl(state.redirect.link) !== window.location.href) {
					navigate(state.redirect.link, state.redirect.options);
				}

				if (onReturn) {
					// NOTE:
					// https://www.test.sparebank1.no/test/mbc-demo/static/docs/tutorial-03-open_web_page_v2.html#sequence-diagram
					// onReturn callback-en skjer med en gang, men det tar 300 ms å lukke forrige side, så denne vil bli kjørt mens forrige side fortsatt er oppe.
					// Dette kan skape bugs siden kode vi kjører i onReturn-en kan hende ikke burde bli kjørt på forrige side
					setTimeout(() => onReturn(data), 300);
				}
			});
		}
	};

	return (link: string | number, opts?: MbcOptions) => navigate(link, opts);
}
export default useNavigateMBC;

export function useOnWebPageClosed(callback?: () => void) {
	const [ikkeForsteSide] = useState(getMobilBankState().ikkeForsteSide);
	const reduxState = useStore<State>().getState();

	if (mbc.isNative()) {
		mbc.onClosed(() => {
			callback?.();
			return ikkeForsteSide ? { reduxState, storage: mbcStorage.storage, debugLogs: debugLogger.logs } : {};
		});
	}
}
